import React from 'react';
import { Link } from 'react-router-dom';

interface QuotaBannerProps {
  subscriptionData?: {
    has_active_subscription: boolean;
    subscription_plan_key: string;
    subscription_plan_name: string;
    plan_color?: string;
    features?: {
      custom_quiz?: {
        enabled: boolean;
        limit: number | null;
        current_usage: number;
        remaining: number | null;
        unlimited: boolean;
        quota_exceeded: boolean;
      };
    };
  };
  loading?: boolean;
}

const QuotaBanner: React.FC<QuotaBannerProps> = ({ subscriptionData, loading }) => {
  if (loading) {
    return (
      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
        <div className="card-body p-4">
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm text-primary me-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div>
              <h6 className="mb-1">Loading quota information...</h6>
              <small className="text-muted">Please wait while we check your quiz limits.</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionData || !subscriptionData.features?.custom_quiz) {
    return null;
  }

  const customQuiz = subscriptionData.features.custom_quiz;
  const planKey = subscriptionData.subscription_plan_key?.toLowerCase();
  const planName = subscriptionData.subscription_plan_name || 'Free';

  // Get plan-specific colors
  const getPlanColors = () => {
    switch (planKey) {
      case 'free':
        return { primary: '#6c757d', bg: '#f8f9fa', border: '#dee2e6' };
      case 'basic':
        return { primary: '#0d6efd', bg: '#cfe2ff', border: '#9ec5fe' };
      case 'premium':
        return { primary: '#6f42c1', bg: '#e0cffc', border: '#c29ffa' };
      case 'pro':
        return { primary: '#fd7e14', bg: '#ffe5d0', border: '#ffc9a0' };
      default:
        return { primary: subscriptionData.plan_color || '#6c757d', bg: '#f8f9fa', border: '#dee2e6' };
    }
  };

  const colors = getPlanColors();
  const isUnlimited = customQuiz.unlimited || customQuiz.limit === null || customQuiz.limit === -1;
  const remaining = customQuiz.remaining || 0;
  const total = customQuiz.limit || 0;
  const isQuotaExceeded = customQuiz.quota_exceeded || remaining === 0;
  const isApproachingLimit = !isUnlimited && remaining > 0 && remaining <= 5;

  // Determine banner style based on quota status
  const getBannerStyle = () => {
    if (isQuotaExceeded) {
      return {
        bgColor: '#fff5f5',
        borderColor: '#fed7d7',
        iconColor: '#dc3545',
        icon: 'fas fa-exclamation-triangle',
        titleColor: '#dc3545'
      };
    }
    
    if (isUnlimited) {
      return {
        bgColor: colors.bg,
        borderColor: colors.border,
        iconColor: colors.primary,
        icon: 'fas fa-infinity',
        titleColor: colors.primary
      };
    }
    
    if (isApproachingLimit) {
      return {
        bgColor: '#fffbf0',
        borderColor: '#feebc8',
        iconColor: '#ffc107',
        icon: 'fas fa-exclamation-circle',
        titleColor: '#ffc107'
      };
    }
    
    return {
      bgColor: '#f0f9ff',
      borderColor: '#bae6fd',
      iconColor: '#0dcaf0',
      icon: 'fas fa-info-circle',
      titleColor: '#0dcaf0'
    };
  };

  const style = getBannerStyle();

  const getTitle = () => {
    if (isQuotaExceeded) {
      return `${planName} Plan - Quiz Limit Reached`;
    }
    if (isUnlimited) {
      return `${planName} Plan - Unlimited Quiz Access`;
    }
    if (isApproachingLimit) {
      return `${planName} Plan - Approaching Quiz Limit`;
    }
    return `${planName} Plan - Quiz Quota Status`;
  };

  const getMessage = () => {
    if (isQuotaExceeded) {
      return (
        <>
          You have reached your custom quiz limit of <strong>{total}</strong> quizzes.
          {planKey === 'free' && (
            <> Upgrade to <strong>Basic</strong>, <strong>Premium</strong>, or <strong>Pro</strong> plan for unlimited custom quizzes!</>
          )}
        </>
      );
    }
    
    if (isUnlimited) {
      return (
        <>
          🎉 You have <strong>unlimited</strong> access to create custom quizzes. 
          Enjoy your {planName} experience! You've created <strong>{customQuiz.current_usage}</strong> quiz{customQuiz.current_usage !== 1 ? 'es' : ''} so far.
        </>
      );
    }
    
    if (isApproachingLimit) {
      return (
        <>
          ⚠️ You have <strong>{remaining}</strong> quiz{remaining !== 1 ? 'es' : ''} remaining 
          out of <strong>{total}</strong> total ({customQuiz.current_usage} used).
          {planKey === 'free' && (
            <> Consider upgrading to <strong>Basic</strong> or higher plan for unlimited quizzes!</>
          )}
        </>
      );
    }
    
    return (
      <>
        You have <strong>{remaining}</strong> quiz{remaining !== 1 ? 'es' : ''} remaining 
        out of <strong>{total}</strong> total. You've used <strong>{customQuiz.current_usage}</strong> quiz{customQuiz.current_usage !== 1 ? 'es' : ''}.
      </>
    );
  };

  const getActionButton = () => {
    if (isQuotaExceeded || (planKey === 'free' && isApproachingLimit)) {
      return (
        <Link 
          to="/pricing"
          className="btn btn-sm shadow-sm"
          style={{
            backgroundColor: isQuotaExceeded ? '#dc3545' : colors.primary,
            color: 'white',
            border: 'none',
            padding: '8px 20px',
            fontWeight: '600',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap'
          }}
        >
          <i className="fas fa-crown me-2"></i>
          Upgrade Now
        </Link>
      );
    }
    return null;
  };

  // Show progress bar for limited plans
  const showProgressBar = !isUnlimited && total > 0;
  const usageCount = customQuiz.current_usage || 0;
  // Calculate percentage, cap at 100% for display
  const rawPercentage = showProgressBar ? (usageCount / total) * 100 : 0;
  const progressPercentage = Math.min(rawPercentage, 100);
  const progressColor = isQuotaExceeded || progressPercentage >= 90 ? '#dc3545' : 
                        progressPercentage >= 70 ? '#ffc107' : 
                        colors.primary;

  return (
    <div 
      className="card border-0 shadow-sm mb-4" 
      style={{ 
        borderRadius: '15px',
        backgroundColor: style.bgColor,
        borderLeft: `5px solid ${style.iconColor}`
      }}
    >
      <div className="card-body p-4">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-start flex-grow-1" style={{ minWidth: '0' }}>
            <div className="me-3 mt-1" style={{ flexShrink: 0 }}>
              <i 
                className={`${style.icon} fa-lg`}
                style={{ color: style.iconColor }}
              ></i>
            </div>
            <div className="flex-grow-1" style={{ minWidth: '0' }}>
              <h6 className="mb-2 fw-bold" style={{ color: style.titleColor }}>
                {getTitle()}
              </h6>
              <p className="mb-2 text-muted" style={{ fontSize: '0.95rem' }}>
                {getMessage()}
              </p>
              
              {/* Progress Bar for Limited Plans */}
              {showProgressBar && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted fw-semibold">Usage</small>
                    <small className={`fw-bold ${isQuotaExceeded ? 'text-danger' : 'text-muted'}`}>
                      {usageCount} / {total}
                    </small>
                  </div>
                  <div className="progress" style={{ height: '10px', borderRadius: '10px', backgroundColor: '#e9ecef' }}>
                    <div 
                      className="progress-bar"
                      role="progressbar"
                      style={{ 
                        width: `${progressPercentage}%`,
                        backgroundColor: progressColor,
                        borderRadius: '10px',
                        transition: 'width 0.6s ease'
                      }}
                      aria-valuenow={progressPercentage}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  {isQuotaExceeded && (
                    <div className="mt-2">
                      <small className="text-danger">
                        <i className="fas fa-info-circle me-1"></i>
                        You've exceeded your limit by {usageCount - total} quiz{(usageCount - total) !== 1 ? 'es' : ''}
                      </small>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="d-flex align-items-start" style={{ flexShrink: 0 }}>
            {getActionButton()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotaBanner;